<template>
  <div>
    <v-row class="spacing-lg-bottom">
      <v-col cols="12">
        <h2 class="subpage-heading">
          {{ t('pension_payment_overview') }}
        </h2>
      </v-col>

      <v-col cols="12" class="lifeyris-table">
        <LifeyrisgreidsluTable
          :lifeyrisgreidslur="lifeyrisGreidslur"
          :loading="loading"
          class="subpage__table subpage__table-default"
          @open-year="openYearFromOverview"
        />
      </v-col>
    </v-row>
    <v-row class="mb-8">
      <v-col cols="12">
        <h2 class="subpage-heading">
          {{ t('lifeyrirundirsida_text_header') }}
        </h2>
      </v-col>
      

      <v-col cols="12" class="lifeyris-table">
        <v-row>
          <v-col md="3" offset-md="9" sm="6" offset-sm="6">
            <v-select
              v-model="year"
              :items="years"
              :label="t('premiums_choose_year')"
              variant="outlined"
              density="compact"
              clearable
              hide-details="auto"
              @update:model-value="onYearChanged"
            />
          </v-col>
        </v-row>

        <Lifeyrisgreidslufaerslur
          :lifeyrisgreidslur="lifeyrisGreidslurArs"
          :loading="loading"
          @open-month="openMonth"
          class="subpage__table subpage__table-default"
        />
      </v-col>
    </v-row>

    <!-- Inline details for the selected month (mirrors legacy behavior) -->
    <div v-if="isOpen" ref="sundurlidunEl" class="mt-10">
      <h3>
        {{ t('lifeyrisTable_sundurlidun_header') }}
        {{ currentSundurlidun?.arMan }}
      </h3>

      <v-data-table
        :headers="sundurlidunHeaders"
        :items="sundurlidun"
        :loading="loading"
        loading-text="Sæki gögn..."
        no-data-text="Engin gögn fundust"
        hide-default-footer
        :mobile-breakpoint="0"
        class="mb-10"
        hover
      >
        <template #item.upphaed="{ item }">
          <div class="text-right">{{ currency(item.upphaed) }}</div>
        </template>
        <template #bottom />
        <template #body.append>
          <tr>
            <td>{{ t('lifeyrisTable_stadgreidslu_samtals') }}</td>
            <td />
            <td />
            <td />
            <td class="text-right">{{ currency(getTotalSundurlidun()) }}</td>
          </tr>
        </template>
      </v-data-table>

      <h3 class="mt-10">
        {{ t('lifeyrisTable_stadgreidslu_header') }}
      </h3>
      <v-data-table
        :headers="stadgreidsluHeaders"
        :items="stadgreidsla"
        :loading="loading"
        loading-text="Sæki gögn..."
        no-data-text="Engin gögn fundust"
        hide-default-footer
        :mobile-breakpoint="0"
        class="mb-10"
        hover
      >
        <template #item.persAfslArs="{ item }">
          <div class="text-right">{{ currency(item.persAfslArs) }}</div>
        </template>
        <template #item.nytturPersAfslArs="{ item }">
          <div class="text-right">{{ currency(item.nytturPersAfslArs) }}</div>
        </template>
        <template #item.hatekjuSkattar="{ item }">
          <div class="text-right">{{ currency(item.hatekjuSkattar) }}</div>
        </template>
        <template #item.stadgreidsla="{ item }">
          <div class="text-right">{{ currency(item.stadgreidsla) }}</div>
        </template>
        <template #bottom />
        <template #body.append>
          <tr>
            <td>{{ t('lifeyrisTable_stadgreidslu_samtals') }}</td>
            <td />
            <td />
            <td />
            <td />
            <td class="text-right">{{ currency(getTotalStadgreidsla()) }}</td>
          </tr>
        </template>
      </v-data-table>

      <h3 class="mt-10">
        {{ t('lifeyrisTable_utborgun_header') }}
      </h3>
      <v-data-table
        :headers="utborgunHeaders"
        :items="utborgun"
        :loading="loading"
        loading-text="Sæki gögn..."
        no-data-text="Engin gögn fundust"
        hide-default-footer
        :mobile-breakpoint="0"
        hover
      >
        <template #item.dagsetning="{ item }">
          {{ formatDate(item.dagsetning, locale) }}
        </template>
        <template #item.upphaed="{ item }">
          <div class="text-right">{{ currency(item.upphaed) }}</div>
        </template>
        <template #bottom />
        <template #body.append>
          <tr>
            <td>{{ t('lifeyrisTable_stadgreidslu_samtals') }}</td>
            <td />
            <td />
            <td class="text-right">{{ currency(getTotalUtborgun()) }}</td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useJoakimStore } from '@/stores/useJoakimStore'
import { useCurrency } from '@/composables/useCurrency'
import { formatDate } from '@/filters/date'
import LifeyrisgreidsluTable from '../components/pensionPayments/LifeyrisgreidsluTable.vue'
import Lifeyrisgreidslufaerslur from '../components/pensionPayments/Lifeyrisgreidslufaerslur.vue'

const { t, locale } = useI18n()
const { currency } = useCurrency()
const route = useRoute()
const joakim = useJoakimStore()

const loading = ref(false)
const years = ref<number[]>([])
const year = ref<number | null>(null)
const lifeyrisGreidslur = ref<any[]>([])     // per-year overview
const lifeyrisGreidslurArs = ref<any[]>([]) // per-month rows for selected year

// inline detail area (per selected month)
const isOpen = ref(false)
const currentSundurlidun = ref<any | null>(null)
const sundurlidun = ref<any[]>([])
const stadgreidsla = ref<any[]>([])
const utborgun = ref<any[]>([])
const sundurlidunEl = ref<HTMLElement | null>(null)

const now = new Date()
const january = now.getMonth() === 0
const initialYear = january ? now.getFullYear() - 1 : now.getFullYear()

onMounted(async () => {
  loading.value = true

  // fetch overview
  const result = await joakim.getGreidslusogu()
  if (Array.isArray(result?.greidslusaga) && result.greidslusaga.length) {
    lifeyrisGreidslur.value = [...result.greidslusaga]
      .sort((a, b) => (a.ar ?? 0) - (b.ar ?? 0))
      .reverse()
  }
  if (Array.isArray(result?.year) && result.year.length) {
    // ensure numeric sort (API may send strings)
    years.value = result.year
      .map((y: any) => Number(y))
      .filter((y: number) => Number.isFinite(y))
      .sort((a: number, b: number) => a - b)
      .reverse()
  }

  // initial year: route param takes precedence if valid
  const routeYear = Number(route.params.id)
  if (Number.isFinite(routeYear)) {
    year.value = routeYear
  } else {
    year.value = initialYear
  }

  await loadYear(year.value)
  loading.value = false
})

watch(year, async (val, oldVal) => {
  if (val === oldVal) return
  resetDetails()
  if (val == null) {
    // if cleared, bounce back to the initial default year
    year.value = initialYear
    return
  }
  loading.value = true
  await loadYear(val)
  loading.value = false
})

function onYearChanged(val: number | null) {
  // guard against NaN if a string slips through
  if (typeof val === 'string') {
    const n = Number(val)
    year.value = Number.isFinite(n) ? n : initialYear
  } else {
    year.value = val
  }
}

async function loadYear(y: number | null) {
  if (y == null) return
  const res = await joakim.getGreidslusoguArs(y)
  lifeyrisGreidslurArs.value = (res?.greidslur ?? [])
    .slice()
    .sort((a: any, b: any) => (a.arMan > b.arMan ? 1 : -1))
    .reverse()
}

function openYearFromOverview(row: any) {
  const y = Number(row?.ar)
  if (Number.isFinite(y)) year.value = y
}

function openMonth(item: any) {
  resetDetails()
  currentSundurlidun.value = item
  sundurlidun.value = item?.greidslurPerSjod ?? []
  stadgreidsla.value = item?.stadgreidsla ?? []
  utborgun.value = item?.utborgun ?? []
  isOpen.value = true

  // smooth scroll like the old vue-scrollto
  nextTick(() => {
    sundurlidunEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

function resetDetails() {
  isOpen.value = false
  currentSundurlidun.value = null
  sundurlidun.value = []
  stadgreidsla.value = []
  utborgun.value = []
}

// computed headers for the inline detail tables
const sundurlidunHeaders = computed(() => [
  { title: t('lifeyrisTable_sundurlidun_sjodur'), key: 'sjodur', sortable: false },
  { title: t('lifeyrisTable_sundurlidun_tegund'), key: 'tegund', sortable: false },
  { title: t('lifeyrisTable_sundurlidun_skyring'), key: 'skyring', sortable: false },
  { title: t('lifeyrisTable_sundurlidun_fyrir'), key: 'fyrir', sortable: false },
  { title: t('lifeyrisTable_sundurlidun_upphaed'), key: 'upphaed', align: 'end' as const, sortable: false },
])

const stadgreidsluHeaders = computed(() => [
  { title: t('lifeyrisTable_stadgreidslu_hlutfall'), key: 'skattkortHlutfall', sortable: false },
  { title: t('lifeyrisTable_stadgreidslu_makahlutfall'), key: 'skattkortMakaHlutfall', sortable: false },
  { title: t('lifeyrisTable_stadgreidslu_personuafslattur'), key: 'persAfslArs', align: 'end' as const, sortable: false },
  { title: t('lifeyrisTable_stadgreidslu_nyturafslattur'), key: 'nytturPersAfslArs', align: 'end' as const, sortable: false },
  { title: t('lifeyrisTable_stadgreidslu_hatekjuskattur'), key: 'hatekjuSkattar', align: 'end' as const, sortable: false },
  { title: t('lifeyrisTable_stadgreidslu_samtals'), key: 'stadgreidsla', align: 'end' as const, sortable: false },
])

const utborgunHeaders = computed(() => [
  { title: t('lifeyrisTable_utborgun_reikningur'), key: 'reikningur', sortable: false },
  { title: t('lifeyrisTable_utborgun_banki'), key: 'banki', sortable: false },  // fixed typo
  { title: t('lifeyrisTable_utborgun_dagsetning'), key: 'dagsetning', sortable: false },
  { title: t('lifeyrisTable_utborgun_upphaed'), key: 'upphaed', align: 'end' as const, sortable: false },
])

// totals
function getTotalSundurlidun() {
  if (!currentSundurlidun.value) return 0
  return (currentSundurlidun.value.greidslurPerSjod ?? [])
    .reduce((a: number, x: any) => a + (Number(x?.upphaed) || 0), 0)
}
function getTotalStadgreidsla() {
  if (!currentSundurlidun.value) return 0
  return (currentSundurlidun.value.stadgreidsla ?? [])
    .reduce((a: number, x: any) => a + (Number(x?.stadgreidsla) || 0), 0)
}
function getTotalUtborgun() {
  if (!currentSundurlidun.value) return 0
  return (currentSundurlidun.value.utborgun ?? [])
    .reduce((a: number, x: any) => a + (Number(x?.upphaed) || 0), 0)
}

const currentSundurlidunExpose = computed(() => currentSundurlidun.value)
</script>

<style scoped lang="scss">
.lifeyris-table {
  box-shadow: 0 4px 12px rgba(0,0,0,.08), 0 10px 30px rgba(0,0,0,.04);
}
.subpage-heading { margin-bottom: .5rem; }
.text-right { text-align: right; }
</style>
